// View geometry: how wide to frame an area, and the box that results.
//
// This file used to define "theaters" — quantised operating rectangles snapped to
// a coarse grid, each with a canonical key. All of that existed for ONE reason: an
// arbitrary click-bbox is unique per click, so a server-side cache of per-area
// field tiles would never get a second hit, and snapping collapsed the key space.
//
// The field is now served as windows out of one large pre-fetched box, so there
// are no per-area tiles to key and nothing left to quantise. The snap grid, the
// keys, the containment tests and the "is this the same theater?" comparisons are
// all deleted rather than left lying around to confuse the next reader.
//
// What survives is the part that was always genuinely useful: three widths to
// frame a view at, and the arithmetic turning a centre plus a width into a box.

/** View widths, widest first.
 *
 *  The ladder floors at 250 nm because GFS-Wave resolves at 0.25 deg (~15 nm). A
 *  60 nm rung would render four model cells across — an interpolated blur
 *  presented as detail — so the floor sits where the data stops resolving rather
 *  than at an arbitrary zoom. These are PRESETS, not limits: the map is free, and
 *  you can zoom wherever you like afterwards. */
export const RUNGS = [
  { id: "basin", label: "Wide", spanNm: 1200 },
  { id: "regional", label: "Regional", spanNm: 600 },
  { id: "operational", label: "Working", spanNm: 250 },
] as const;

export type RungId = (typeof RUNGS)[number]["id"];

/** The width a view opens at unless an area asks for something else. */
export const DEFAULT_RUNG: RungId = "operational";

export interface Bounds {
  latMin: number;
  latMax: number;
  lonMin: number;
  lonMax: number;
}

/** Nautical miles per degree of latitude. */
const NM_PER_DEG = 60;

export function rung(id: RungId): (typeof RUNGS)[number] {
  return RUNGS.find((r) => r.id === id) ?? RUNGS[RUNGS.length - 1];
}

export function isRungId(v: unknown): v is RungId {
  return typeof v === "string" && RUNGS.some((r) => r.id === v);
}

/** The rung whose width is closest to `nm`. */
export function rungForSpan(nm: number): RungId {
  let best: RungId = DEFAULT_RUNG;
  let bestGap = Infinity;
  for (const r of RUNGS) {
    const gap = Math.abs(r.spanNm - nm);
    if (gap < bestGap) {
      bestGap = gap;
      best = r.id;
    }
  }
  return best;
}

/**
 * The box `spanNm` wide centred on a point.
 *
 * `aspect` is width/height. The latitude span is the width in degrees; the
 * longitude span is widened by the aspect and divided by cos(lat) so a nautical
 * mile is the same length on both axes — without that, a box drawn in the Gulf
 * would be visibly squashed against one drawn off Angola.
 */
export function boundsFor(lat: number, lon: number, spanNm: number, aspect = 1): Bounds {
  const latSpan = spanNm / NM_PER_DEG;
  // Guard the cosine near the poles; TDI operates well inside this, but an
  // unguarded division would blow the box up to the whole globe at 90 deg.
  const cos = Math.max(0.15, Math.cos((lat * Math.PI) / 180));
  const lonSpan = (latSpan * Math.max(0.1, aspect)) / cos;
  return {
    latMin: lat - latSpan / 2,
    latMax: lat + latSpan / 2,
    lonMin: lon - lonSpan / 2,
    lonMax: lon + lonSpan / 2,
  };
}

/** Pull a point inside a box — keeps a dragged site within the area it belongs to. */
export function clampToBounds(b: Bounds, lat: number, lon: number): { lat: number; lon: number } {
  return {
    lat: Math.max(b.latMin, Math.min(b.latMax, lat)),
    lon: Math.max(b.lonMin, Math.min(b.lonMax, lon)),
  };
}
